import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Shield, Users, Database, Activity, Settings, UserCheck, UserX, Crown, Mail, Calendar, Edit, Trash2, Eye, Zap, Rss } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const AdminDashboard = () => {
  const { user, isAdmin } = useAuth();
  const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
  const [activeTab, setActiveTab] = useState('overview');
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    adminUsers: 0,
    recentSignups: 0
  });
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Redirect if not admin
  useEffect(() => {
    if (!isAdmin) {
      window.location.href = '/';
      return;
    }
  }, [isAdmin]);

  // Fetch admin data
  useEffect(() => {
    console.log('AdminDashboard useEffect triggered. isAdmin:', isAdmin, 'user:', user);
    
    const fetchAdminData = async () => {
      try {
        const token = localStorage.getItem('videbits-token');
        console.log('Token from localStorage:', token ? 'Token found' : 'No token');
        
        if (!token) {
          throw new Error('No authentication token');
        }

        const headers = {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        };

        console.log('Making API calls to backend...');

        // Fetch users, stats, and activity in parallel
        const [usersResponse, statsResponse, activityResponse] = await Promise.all([
          fetch(`${API_BASE}/admin/users`, { headers }),
          fetch(`${API_BASE}/admin/stats`, { headers }),
          fetch(`${API_BASE}/admin/activity?limit=5`, { headers })
        ]);

        console.log('Response status:', {
          users: usersResponse.status,
          stats: statsResponse.status,
          activity: activityResponse.status
        });

        if (!usersResponse.ok || !statsResponse.ok || !activityResponse.ok) {
          throw new Error('Failed to fetch admin data');
        }

        const usersData = await usersResponse.json();
        const statsData = await statsResponse.json();
        const activityData = await activityResponse.json();

        console.log('Data received:', {
          users: usersData.users?.length,
          stats: statsData,
          activity: activityData.activity?.length
        });

        setUsers(usersData.users);
        setStats(statsData);
        setActivity(activityData.activity || []);
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
        setLoading(false);
      }
    };

    if (isAdmin) {
      console.log('User is admin, fetching data...');
      fetchAdminData();
    } else {
      console.log('User is not admin or not loaded yet');
    }
  }, [isAdmin, user]);

  // Filter users based on search
  const filteredUsers = users.filter(user =>
    user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    `${user.firstName} ${user.lastName}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRelativeTime = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);
    
    if (diffInDays > 0) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else if (diffInHours > 0) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else {
      const diffInMinutes = Math.floor(diffInMs / (1000 * 60));
      return `${diffInMinutes} minute${diffInMinutes > 1 ? 's' : ''} ago`;
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      const token = localStorage.getItem('videbits-token');
      if (!token) {
        alert('No authentication token');
        return;
      }

      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      let response;
      let successMessage;

      switch (action) {
        case 'view':
          // Navigate to user profile view (placeholder)
          alert(`View user feature coming soon`);
          break;
          
        case 'edit':
          // Navigate to user edit form (placeholder)
          alert(`Edit user feature coming soon`);
          break;
          
        case 'activate':
          response = await fetch(`${API_BASE}/admin/users/${userId}/status`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ is_active: true })
          });
          successMessage = 'User activated successfully';
          break;

        case 'deactivate':
          response = await fetch(`${API_BASE}/admin/users/${userId}/status`, {
            method: 'PUT',
            headers,
            body: JSON.stringify({ is_active: false })
          });
          successMessage = 'User deactivated successfully';
          break;

        case 'delete':
          if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
            return;
          }

          response = await fetch(`${API_BASE}/admin/users/${userId}`, {
            method: 'DELETE',
            headers
          });
          successMessage = 'User deleted successfully';
          break;
          
        default:
          console.log(`${action} user ${userId}`);
          return;
      }

      if (response) {
        if (response.ok) {
          alert(successMessage);
          // Refresh user data
          window.location.reload();
        } else {
          const error = await response.json();
          alert(`Error: ${error.detail || 'Operation failed'}`);
        }
      }
    } catch (error) {
      console.error('User action failed:', error);
      alert(`Error: ${error.message}`);
    }
  };

  if (!isAdmin) {
    return null; // Will redirect in useEffect
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>Back to Home</span>
              </Link>
              <div className="h-6 w-px bg-gray-300"></div>
              <div className="flex items-center space-x-3">
                <Shield className="w-6 h-6 text-videbits-600" />
                <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="flex items-center space-x-2 bg-videbits-100 px-3 py-1 rounded-full">
                <Crown className="w-4 h-4 text-videbits-600" />
                <span className="text-sm text-videbits-700 font-medium">
                  {user?.firstName} {user?.lastName}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Navigation Tabs */}
        <div className="mb-8">
          <nav className="flex space-x-8">
            {[
              { id: 'overview', label: 'Overview', icon: Activity },
              { id: 'users', label: 'User Management', icon: Users },
              { id: 'system', label: 'System Settings', icon: Settings }
            ].map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`flex items-center space-x-2 px-4 py-2 border-b-2 transition-colors ${
                  activeTab === id
                    ? 'border-videbits-600 text-videbits-600'
                    : 'border-transparent text-videbits-500 hover:text-videbits-700 hover:border-videbits-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                <span className="font-medium">{label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="space-y-8">
            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { label: 'Total Users', value: stats.totalUsers, icon: Users, color: 'purple' },
                { label: 'Active Users', value: stats.activeUsers, icon: UserCheck, color: 'green' },
                { label: 'Admin Users', value: stats.adminUsers, icon: Shield, color: 'purple' },
                { label: 'Recent Signups', value: stats.recentSignups, icon: Calendar, color: 'orange' }
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-gray-600">{label}</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">{value}</p>
                    </div>
                    <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                      <Icon className={`w-6 h-6 text-${color}-600`} />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Management Tools */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Management Tools</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Link
                  to="/admin/notifications"
                  className="group flex items-center p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-videbits-500 hover:bg-videbits-50 transition-all duration-200"
                >
                  <div className="w-10 h-10 bg-videbits-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-videbits-200 transition-colors">
                    <Mail className="w-5 h-5 text-videbits-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 group-hover:text-videbits-900">
                      📧 Notifications Management
                    </h3>
                    <p className="text-sm text-gray-600 group-hover:text-videbits-700">
                      Manage email templates, notifications, and delivery analytics
                    </p>
                  </div>
                </Link>

                <Link
                  to="/admin/email"
                  className="group flex items-center p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-videbits-500 hover:bg-videbits-50 transition-all duration-200"
                >
                  <div className="w-10 h-10 bg-videbits-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-videbits-200 transition-colors">
                    <Mail className="w-5 h-5 text-videbits-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 group-hover:text-videbits-900">
                      📨 Email &amp; release digest
                    </h3>
                    <p className="text-sm text-gray-600 group-hover:text-videbits-700">
                      Subscribers, digest schedule, and send-now controls
                    </p>
                  </div>
                </Link>

                <Link
                  to="/admin/insider"
                  className="group flex items-center p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-videbits-500 hover:bg-videbits-50 transition-all duration-200"
                >
                  <div className="w-10 h-10 bg-videbits-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-videbits-200 transition-colors">
                    <Zap className="w-5 h-5 text-videbits-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 group-hover:text-videbits-900">
                      ⚡ Insider submissions
                    </h3>
                    <p className="text-sm text-gray-600 group-hover:text-videbits-700">
                      Review and approve user-contributed news tips
                    </p>
                  </div>
                </Link>

                <Link
                  to="/admin/feeds"
                  className="group flex items-center p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-videbits-500 hover:bg-videbits-50 transition-all duration-200"
                >
                  <div className="w-10 h-10 bg-videbits-100 rounded-lg flex items-center justify-center mr-4 group-hover:bg-videbits-200 transition-colors">
                    <Rss className="w-5 h-5 text-videbits-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900 group-hover:text-videbits-900">
                      📡 News feeds
                    </h3>
                    <p className="text-sm text-gray-600 group-hover:text-videbits-700">
                      Add or disable the RSS sources the nightly ingest reads
                    </p>
                  </div>
                </Link>

                <div className="group flex items-center p-4 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                    <Database className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-500">
                      🗄️ Database Management
                    </h3>
                    <p className="text-sm text-gray-400">
                      Coming soon - Database analytics and maintenance
                    </p>
                  </div>
                </div>
                
                <div className="group flex items-center p-4 rounded-lg border-2 border-dashed border-gray-200 bg-gray-50">
                  <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center mr-4">
                    <Settings className="w-5 h-5 text-gray-400" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-500">
                      ⚙️ System Configuration
                    </h3>
                    <p className="text-sm text-gray-400">
                      Coming soon - System settings and configuration
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Activity</h2>
              {activity.length > 0 ? (
                <div className="space-y-4">
                  {activity.map((item, index) => (
                    <div key={index} className="flex items-center space-x-3 py-2">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <Activity className="w-4 h-4 text-gray-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-gray-900">
                          <span className="font-medium">{item.user}</span>{' '}
                          {item.type === 'user_signup' && 'signed up'}
                          {item.type === 'user_login' && 'logged in'}
                          {item.type === 'admin_action' && 'performed admin action'}
                        </p>
                        <p className="text-xs text-gray-500">{getRelativeTime(item.time)}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">No recent activity available</p>
                  <p className="text-sm text-gray-400 mt-2">User activity will appear here when available</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* Search and Filters */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search users by name, username, or email..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-videbits-500"
                  />
                </div>
              </div>
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Users ({filteredUsers.length})</h2>
              </div>
              
              {loading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-videbits-600"></div>
                  <p className="mt-2 text-gray-500">Loading users...</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Joined</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Login</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {filteredUsers.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="w-10 h-10 bg-videbits-600 rounded-full flex items-center justify-center text-white font-medium">
                                {user.firstName?.charAt(0)}{user.lastName?.charAt(0)}
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {user.firstName} {user.lastName}
                                </div>
                                <div className="text-sm text-gray-500">@{user.username}</div>
                                <div className="text-sm text-gray-500">{user.email}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              user.role === 'admin'
                                ? 'bg-videbits-100 text-videbits-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {user.role === 'admin' && <Shield className="w-3 h-3 mr-1" />}
                              {user.role}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center space-x-2">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                user.isActive
                                  ? 'bg-green-100 text-green-800'
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {user.isActive ? <UserCheck className="w-3 h-3 mr-1" /> : <UserX className="w-3 h-3 mr-1" />}
                                {user.isActive ? 'Active' : 'Inactive'}
                              </span>
                              {user.emailVerified && (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-videbits-100 text-videbits-800">
                                  <Mail className="w-3 h-3 mr-1" />
                                  Verified
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.createdAt)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(user.lastLogin)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button
                                onClick={() => handleUserAction(user.id, 'view')}
                                className="text-gray-400 cursor-not-allowed opacity-50"
                                title="View user (Coming Soon)"
                                disabled
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleUserAction(user.id, 'edit')}
                                className="text-gray-400 cursor-not-allowed opacity-50"
                                title="Edit user (Coming Soon)"
                                disabled
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              {user.role !== 'admin' && (
                                <>
                                  {user.isActive ? (
                                    <button
                                      onClick={() => handleUserAction(user.id, 'deactivate')}
                                      className="text-orange-600 hover:text-orange-900"
                                      title="Deactivate user"
                                    >
                                      <UserX className="w-4 h-4" />
                                    </button>
                                  ) : (
                                    <button
                                      onClick={() => handleUserAction(user.id, 'activate')}
                                      className="text-green-600 hover:text-green-900"
                                      title="Activate user"
                                    >
                                      <UserCheck className="w-4 h-4" />
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleUserAction(user.id, 'delete')}
                                    className="text-red-600 hover:text-red-900"
                                    title="Delete user"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* System Settings Tab */}
        {activeTab === 'system' && (
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center space-x-3 mb-6">
                <Database className="w-5 h-5 text-gray-500" />
                <h2 className="text-lg font-semibold text-gray-900">System Configuration</h2>
              </div>
              
              <div className="space-y-4">
                <div className="border-b border-gray-100 pb-4">
                  <h3 className="text-sm font-medium text-gray-900 mb-2">Database Status</h3>
                  <div className="flex items-center space-x-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                    <span className="text-sm text-gray-600">Connected and operational</span>
                  </div>
                </div>
                
                <div className="border-b border-gray-100 pb-4 opacity-50">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">System Maintenance</h3>
                  <p className="text-sm text-gray-400 mb-3">Perform system maintenance tasks (Coming Soon)</p>
                  <button
                    disabled
                    className="px-3 py-1 text-sm border border-gray-200 rounded-md bg-gray-100 text-gray-400 cursor-not-allowed"
                  >
                    Run Maintenance
                  </button>
                </div>
                
                <div className="opacity-50">
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Backup & Export</h3>
                  <p className="text-sm text-gray-400 mb-3">Export system data and create backups (Coming Soon)</p>
                  <button
                    disabled
                    className="px-3 py-1 text-sm border border-gray-200 rounded-md bg-gray-100 text-gray-400 cursor-not-allowed"
                  >
                    Export System Data
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
